import type {
  AccountJyotishSnapshot,
  AccountReading,
  AccountRelationshipHome,
} from "./account-api";
import {
  AccountRelationshipActionLink,
  AccountRelationshipTelemetry,
} from "./AccountRelationshipAction";
import { AccountRemedyOutcome } from "./AccountRemedyOutcome";
import { AccountTodayJyotish } from "./AccountTodayJyotish";
import { formatAccountDate } from "./account-format";

export function AccountJyotishiHome({
  readings,
  snapshot,
  home,
  token,
}: {
  readings: AccountReading[];
  snapshot?: AccountJyotishSnapshot | null;
  home?: AccountRelationshipHome | null;
  token: string;
}) {
  const current = readings[0];
  return current ? (
    <ExistingRelationshipHome current={current} snapshot={snapshot} home={home} token={token} />
  ) : (
    <NewRelationshipCard />
  );
}

function ExistingRelationshipHome({
  current,
  snapshot,
  home,
  token,
}: {
  current: AccountReading;
  snapshot?: AccountJyotishSnapshot | null;
  home?: AccountRelationshipHome | null;
  token: string;
}) {
  const { lastActive, jyotishi, language } = relationshipViewContext(current, snapshot, home);

  return (
    <section
      aria-labelledby="your-jyotishi-heading"
      className="mb-6 overflow-hidden rounded-2xl border border-[var(--color-accent-border)] bg-[var(--color-surface-warm)] shadow-[var(--shadow-elevation-medium)]"
    >
      {home ? <AccountRelationshipTelemetry home={home} /> : null}
      <RelationshipHeader jyotishi={jyotishi} opener={home?.remembered_opener} />

      <AccountTodayJyotish
        home={home}
        snapshot={snapshot?.reading_id === current.reading_id ? snapshot : null}
      />
      {home?.pending_outcome ? (
        <div className="px-5 pt-5 sm:px-6">
          <AccountRemedyOutcome followups={[home.pending_outcome]} token={token} />
        </div>
      ) : null}
      <RelationshipContinuation
        current={current}
        home={home}
        language={language}
        lastActive={lastActive}
      />
    </section>
  );
}

function relationshipViewContext(
  current: AccountReading,
  snapshot?: AccountJyotishSnapshot | null,
  home?: AccountRelationshipHome | null
) {
  return {
    lastActive: current.last_message_at || current.created_at,
    jyotishi: current.assigned_jyotishi || "Your Personal AI Jyotishi",
    language: home?.today_guidance?.language || snapshot?.language || current.language || "en",
  };
}

function RelationshipHeader({ jyotishi, opener }: { jyotishi: string; opener?: string | null }) {
  return (
    <div className="border-b border-[var(--color-border-light)] px-5 py-5 sm:px-6">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-accent)]">
        Your AI Jyotishi
      </p>
      <h2 id="your-jyotishi-heading" className="mt-2 font-serif text-3xl leading-tight">
        {jyotishi} remembers where you left off
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-[var(--color-text-sub)]">
        {opener ||
          "Your kundali context and earlier questions stay together, so the next answer continues the same reading instead of starting again."}
      </p>
    </div>
  );
}

function RelationshipContinuation({
  current,
  home,
  language,
  lastActive,
}: {
  current: AccountReading;
  home?: AccountRelationshipHome | null;
  language: string;
  lastActive: string | null;
}) {
  const contextLabel = home?.active_concern
    ? `Current concern · ${formatConcern(home.active_concern)}`
    : "Where you left off";
  const advice = home?.last_advice || current.last_message;
  return (
    <div className="grid gap-5 px-5 py-5 sm:grid-cols-[1fr_auto] sm:items-end sm:px-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-text-faint)]">
          {contextLabel}
        </p>
        <h3 className="mt-2 font-serif text-xl">{current.title}</h3>
        <p className="mt-2 line-clamp-3 text-sm leading-6 text-[var(--color-text-sub)]">
          {advice || "Your kundali is saved and ready for the next question."}
        </p>
        <p className="mt-3 text-xs text-[var(--color-text-faint)]">
          {current.conversation_turns
            ? `${current.conversation_turns} questions remembered`
            : "Kundali context saved"}
          {lastActive ? ` · Last active ${formatAccountDate(lastActive)}` : ""}
        </p>
      </div>
      <RelationshipContinuationAction current={current} home={home} language={language} />
    </div>
  );
}

function RelationshipContinuationAction({
  current,
  home,
  language,
}: {
  current: AccountReading;
  home?: AccountRelationshipHome | null;
  language: string;
}) {
  if (home?.next_action) {
    return (
      <AccountRelationshipActionLink
        action={home.next_action}
        language={language}
        relationshipMeasurementId={home.relationship_measurement_id}
      />
    );
  }
  if (home) return null;
  return (
    <a
      href={current.chat_url}
      className="inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--color-accent)] px-5 py-3 text-sm font-semibold text-white"
    >
      Continue your reading
    </a>
  );
}

function formatConcern(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function NewRelationshipCard() {
  return (
    <section
      aria-labelledby="your-jyotishi-heading"
      className="mb-6 rounded-2xl border border-[var(--color-accent-border)] bg-[var(--color-surface-warm)] p-6 shadow-[var(--shadow-elevation-medium)]"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-accent)]">
        Your AI Jyotishi
      </p>
      <h2 id="your-jyotishi-heading" className="mt-2 font-serif text-3xl leading-tight">
        Why does the same pattern keep returning?
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-[var(--color-text-sub)]">
        Generate your kundali once, then ask every question with the same chart context waiting here
        when you return.
      </p>
      <a
        href="/free-kundali/#kundali-form"
        className="mt-5 inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--color-accent)] px-5 py-3 text-sm font-semibold text-white"
      >
        Generate your kundali
      </a>
    </section>
  );
}
